import { Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AdornisUser, registerUser } from '@adornis/users/db/a-user.js';
import { of, switchMap } from 'rxjs';
import { SmartTeamsAppPartnerCompanyOption } from '../db/SmartTeamsAppPartnerCompanyOption.js';
import { SmartTeamsAppUser } from '../db/SmartTeamsAppUser.js';
import {
  SmartTeamsAppPermission,
  SmartTeamsAppRoleNames,
  SmartTeamsAppUserGroup,
} from '../features/permissions/roles-and-permissions.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';

const USER_GROUP_TO_ROLE: Record<SmartTeamsAppUserGroup, string> = {
  [SmartTeamsAppUserGroup.Admin]: SmartTeamsAppRoleNames.NSC_ADMIN,
  [SmartTeamsAppUserGroup.FieldEngineer]: SmartTeamsAppRoleNames.FIELD_ENGINEER,
  [SmartTeamsAppUserGroup.Customer]: SmartTeamsAppRoleNames.CUSTOMER,
  [SmartTeamsAppUserGroup.PartnerManager]: SmartTeamsAppRoleNames.PARTNER_MANAGER,
};

async function assertUserManagePermission() {
  const allowed = await accessController.can(createPermissionPayload(SmartTeamsAppPermission.UserManage));
  if (!allowed) throw new Error('Insufficient permissions for user management');
}

/**
 * The current user's SmartTeamsAppUser record. Used by the client to drive role-aware UI
 * (hide FE-only buttons when the current user isn't the ticket assignee, etc.).
 */
export const getMyAppUser = registerQuery({
  operationName: 'getMySmartTeamsAppUser',
  type: () => SmartTeamsAppUser,
  params: [],
  resolve: () => async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppUser>) => {
    const userId = (context as any).userID as string | undefined;
    if (!userId) throw new Error('Not authenticated');
    const col = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const me = await col.findOne({ userId });
    if (!me) throw new Error('User profile not found');
    return new SmartTeamsAppUser(me);
  },
});

/**
 * The caller's own partner company, or `null` when they aren't linked to one.
 *
 * Deliberately separate from {@link getSmartTeamsPartnerCompanies}, which lists every company and
 * is admin-only: this returns exactly the one row the caller already belongs to, so it needs no
 * more than an authenticated session. The app user's `companyId` is denormalised from the field
 * engineer's first `partnerCompanyIds` entry, so an FE in several companies resolves to that one.
 */
export const getMyPartnerCompany = registerQuery({
  operationName: 'getMySmartTeamsPartnerCompany',
  type: () => SmartTeamsAppPartnerCompanyOption,
  params: [],
  resolve: () => async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppPartnerCompanyOption>) => {
    const userId = (context as any).userID as string | undefined;
    if (!userId) throw new Error('Not authenticated');

    const userCol = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const me = await userCol.findOne({ userId }, { projection: { companyId: 1 } });
    if (!me?.companyId) return null;

    const companyCol = await getRawCollection<SmartTeamsAppPartnerCompanyOption>('smartteams_partnercompanies');
    const company = await companyCol.findOne({ _id: me.companyId }, { projection: { _id: 1, name: 1 } });
    if (!company) return null;

    return new SmartTeamsAppPartnerCompanyOption({ _id: company._id, name: company.name });
  },
});

export const getSmartTeamsPartnerCompanies = registerQuery({
  operationName: 'getSmartTeamsPartnerCompanies',
  type: () => [SmartTeamsAppPartnerCompanyOption],
  params: [],
  resolve: () => async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppPartnerCompanyOption>) => {
    await assertUserManagePermission();

    const collection = await getRawCollection<SmartTeamsAppPartnerCompanyOption>('smartteams_partnercompanies');
    const companies = await collection
      .find({ archivedAt: null }, { projection: { _id: 1, name: 1 } })
      .sort({ name: 1 })
      .toArray();

    return companies.map(
      company => new SmartTeamsAppPartnerCompanyOption({ _id: company._id, name: company.name }),
    );
  },
});

/**
 * Live subscription for paginated, admin-only user list.
 */
export const getSmartTeamsAppUsers = registerSubscription({
  operationName: 'getSmartTeamsAppUsers',
  type: () => [SmartTeamsAppUser],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  resolve: (skip: number, limit: number, filter?: AdornisFilter) => (_gqlFields: BaseQLSelectionSet<SmartTeamsAppUser>) => {
    return of(null).pipe(
      switchMap(async () => {
        await assertUserManagePermission();
        const userFilter = filter?.getMongoFilter() ?? {};
        return watchQuery<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName, userFilter);
      }),
      switchMap(obs => obs),
    );
  },
});

/**
 * Creates an AdornisUser + SmartTeamsAppUser + assigns the appropriate role.
 */
export const createSmartTeamsAppUser = registerMutation({
  operationName: 'createSmartTeamsAppUser',
  type: () => SmartTeamsAppUser,
  params: [
    { name: 'username', type: () => String },
    { name: 'password', type: () => String },
    { name: 'email', type: () => String },
    { name: 'userGroup', type: () => String },
    { name: 'primaryLanguage', type: () => String },
    { name: 'serviceNowFEId', type: () => String },
    { name: 'companyId', type: () => String },
  ],
  resolve:
    (
      username: string,
      password: string,
      email: string,
      userGroup: string,
      primaryLanguage?: string,
      serviceNowFEId?: string,
      companyId?: string,
    ) =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppUser>) => {
      await assertUserManagePermission();

      if (companyId) {
        const companyCol = await getRawCollection<SmartTeamsAppPartnerCompanyOption>('smartteams_partnercompanies');
        const company = await companyCol.findOne({ _id: companyId }, { projection: { _id: 1 } });
        if (!company) throw new Error(`Unknown partner company: ${companyId}`);
      }

      const group = userGroup as SmartTeamsAppUserGroup;
      if (!Object.values(SmartTeamsAppUserGroup).includes(group)) {
        throw new Error(`Invalid user group: ${userGroup}`);
      }

      // Create or find existing AdornisUser
      let userId: string;
      try {
        userId = await registerUser({ username, email, password, userClass: AdornisUser });
      } catch (err: any) {
        if (err.message?.includes('is already in use')) {
          const userCol = await getRawCollection<AdornisUser>('adornis_users');
          const existing = await userCol.findOne({ username });
          if (!existing) throw new Error('User not found');
          userId = existing._id!;
        } else {
          throw err;
        }
      }

      // Check if SmartTeamsAppUser already exists
      const appUserCol = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
      if (await appUserCol.findOne({ userId })) {
        throw new Error('User already has app access');
      }

      // Assign role
      const roleName = USER_GROUP_TO_ROLE[group];
      const roleCol = await getRawCollection<Role>(Role._collectionName);
      const role = await roleCol.findOne({ roleName });
      if (role) {
        const roleAssignmentCol = await getRawCollection<RoleAssignment>(RoleAssignment._collectionName);
        const existing = await roleAssignmentCol.findOne({ userId });
        if (!existing) {
          await new RoleAssignment({ userId, roleIds: [role._id] }).create();
        } else {
          await roleAssignmentCol.updateOne({ _id: existing._id }, { $addToSet: { roleIds: role._id } });
        }
      }

      // Create SmartTeamsAppUser
      const appUser = new SmartTeamsAppUser({
        userId,
        username,
        email,
        userGroup: group,
        primaryLanguage: primaryLanguage || undefined,
        serviceNowFEId: serviceNowFEId || undefined,
        companyId: companyId || undefined,
        archived: false,
      });
      await appUser.create();

      return appUser;
    },
});

/**
 * Update an existing SmartTeamsAppUser's profile fields.
 */
export const updateSmartTeamsAppUser = registerMutation({
  operationName: 'updateSmartTeamsAppUser',
  type: () => SmartTeamsAppUser,
  params: [
    { name: 'appUserId', type: () => String },
    { name: 'primaryLanguage', type: () => String },
    { name: 'serviceNowFEId', type: () => String },
    { name: 'companyId', type: () => String },
  ],
  resolve:
    (appUserId: string, primaryLanguage?: string, serviceNowFEId?: string, companyId?: string) =>
    async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppUser>) => {
      await assertUserManagePermission();

      if (companyId) {
        const companyCol = await getRawCollection<SmartTeamsAppPartnerCompanyOption>('smartteams_partnercompanies');
        const company = await companyCol.findOne({ _id: companyId }, { projection: { _id: 1 } });
        if (!company) throw new Error(`Unknown partner company: ${companyId}`);
      }

      const collection = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
      const user = await collection.findOne({ _id: appUserId });
      if (!user) throw new Error('User not found');

      const update: Record<string, any> = { updatedAt: new Date() };
      if (primaryLanguage !== undefined) update.primaryLanguage = primaryLanguage || null;
      if (serviceNowFEId !== undefined) update.serviceNowFEId = serviceNowFEId || null;
      if (companyId !== undefined) update.companyId = companyId || null;

      await collection.updateOne({ _id: appUserId }, { $set: update });

      return collection.findOne({ _id: appUserId }) as Promise<SmartTeamsAppUser>;
    },
});

/**
 * Archive a SmartTeamsAppUser — they can no longer log in.
 */
export const archiveSmartTeamsAppUser = registerMutation({
  operationName: 'archiveSmartTeamsAppUser',
  type: () => SmartTeamsAppUser,
  params: [{ name: 'appUserId', type: () => String }],
  resolve: (appUserId: string) => async (_gqlFields: BaseQLSelectionSet<SmartTeamsAppUser>) => {
    await assertUserManagePermission();

    const collection = await getRawCollection<SmartTeamsAppUser>(SmartTeamsAppUser._collectionName);
    const user = await collection.findOne({ _id: appUserId });
    if (!user) throw new Error('User not found');

    await collection.updateOne({ _id: appUserId }, { $set: { archived: true, updatedAt: new Date() } });

    return collection.findOne({ _id: appUserId }) as Promise<SmartTeamsAppUser>;
  },
});
